import React from 'react';
import collide from 'box-collide';

import MapLabel from '../MapLabel';

import { memoizedMeasureTextString as measureTextString } from '../../utilities/measureText.js';
import { transformPoint } from '../../utilities/geometry.js';

const FONT_SIZE = 14;
const DEFAULT_PADDING = 4;
const EXTRA_PADDING = 2;
const POSITIONS = ['c', 't', 'b', 'tl', 'tr', 'bl', 'br', 'cl', 'cr', 'c'];
const DEBUG = false;

function calculateRect(point, textRect, position) {
  switch (position) {
    case 'c':
      return {
        x: point[0] - textRect.hwidth,
        y: point[1] - textRect.hheight,
        width: textRect.width,
        height: textRect.height,
      };
    case 't':
      return {
        x: point[0] - textRect.hwidth,
        y: point[1] - textRect.height,
        width: textRect.width,
        height: textRect.height,
      };
    case 'tl':
      return {
        x: point[0] - textRect.width,
        y: point[1] - textRect.height,
        width: textRect.width,
        height: textRect.height,
      };
    case 'tr':
      return {
        x: point[0],
        y: point[1] - textRect.height,
        width: textRect.width,
        height: textRect.height,
      };
    case 'b':
      return {
        x: point[0] - textRect.hwidth,
        y: point[1],
        width: textRect.width,
        height: textRect.height,
      };
    case 'bl':
      return {
        x: point[0] - textRect.width,
        y: point[1],
        width: textRect.width,
        height: textRect.height,
      };
    case 'br':
      return {
        x: point[0],
        y: point[1],
        width: textRect.width,
        height: textRect.height,
      };
    case 'cl':
      return {
        x: point[0] - textRect.width,
        y: point[1] - textRect.hheight,
        width: textRect.width,
        height: textRect.height,
      };
    case 'cr':
      return {
        x: point[0],
        y: point[1] - textRect.hheight,
        width: textRect.width,
        height: textRect.height,
      };
  }
}

function findPosition(point, textRect, occupiedRects, ignoreOccupiedRectId, alwaysPlace) {
  for (let i = 0; i < POSITIONS.length; i++) {
    const position = POSITIONS[i];
    const rect = calculateRect(point, textRect, position);

    let placed = true;

    for (let j = 0; j < occupiedRects.length; j++) {
      if (
        occupiedRects[j].id &&
        ignoreOccupiedRectId &&
        ignoreOccupiedRectId === occupiedRects[j].id
      ) {
        continue;
      }

      if (collide(rect, occupiedRects[j])) {
        placed = false;
        break;
      }
    }

    if (placed || (alwaysPlace && i === POSITIONS.length - 1)) {
      return {
        position: position,
        textX: position[1] ? (position[1] === 'l' ? point[0] : point[0]) : point[0],
        textY: rect.y,
        textAnchor: position[1] ? (position[1] === 'l' ? 'end' : 'start') : 'middle',
        rect: rect,
      };
    }
  }

  return null;
}

function padRect(rect, padding = EXTRA_PADDING) {
  return {
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
    hwidth: rect.hwidth + padding,
    hheight: rect.hheight + padding,
  };
}

// TODO: Refactor to store occupied rects using a 'bitmap' grid representation of canvas?
// https://idl.cs.washington.edu/papers/fast-labels/
export default function MapLabelsLayer({
  projection,
  transform,
  countries,
  activeCountryIds,
  hoveredCountries,
  showAllLabels,
  onHover,
  onClick,
}) {
  let occupiedRects = [];

  const handleHover = (id) => (event) => {
    if (event.type === 'mouseenter') {
      onHover(id);
    } else {
      onHover(null);
    }
  };

  const handleClick = (id) => () => {
    onClick(id);
  };

  return (
    <g className="map-labels-layer">
      {countries.map((country) => {
        if (!country) {
          return null;
        }

        if (activeCountryIds && !activeCountryIds.includes(country.id)) {
          return null;
        }

        // Use map name only if its one a single ine
        let label =
          country.model.map_name && country.model.map_name.indexOf('\\n') === -1
            ? country.model.map_name
            : country.model.name;

        const point = transformPoint(projection([country.model.lng, country.model.lat]), transform);
        const textRect = measureTextString(label, FONT_SIZE, DEFAULT_PADDING);
        const paddedTextRect = padRect(textRect);

        let position = findPosition(
          point,
          paddedTextRect,
          occupiedRects,
          country.model.id,
          showAllLabels
        );

        if (
          !position &&
          country.model.map_short_name &&
          country.model.map_short_name.toUpperCase() === country.model.map_short_name
        ) {
          const shortTextRect = padRect(
            measureTextString(country.model.map_short_name, FONT_SIZE, DEFAULT_PADDING)
          );
          position = findPosition(
            point,
            shortTextRect,
            occupiedRects,
            country.model.id,
            showAllLabels
          );
          if (position) {
            label = country.model.map_short_name;
          }
        }

        if (position) {
          occupiedRects.push(position.rect);
          return (
            <g
              id={`label-${country.model.id}`}
              transform={`translate(${position.textX} ${position.textY})`}
            >
              <MapLabel
                text={label}
                yOffset={FONT_SIZE}
                textAnchor={position.textAnchor}
                box={country.model.box}
                textRect={textRect}
                active={activeCountryIds ? activeCountryIds.includes(country.model.id) : null}
                hovered={
                  hoveredCountries && hoveredCountries.length && !hoveredCountries.includes(country)
                }
                onHover={onHover && handleHover(country.model.id)}
                onClick={onClick && handleClick(country.model.id)}
              />
            </g>
          );
        }

        return null;
      })}
      {DEBUG &&
        occupiedRects.map((rect) => {
          return (
            <rect
              x={rect.x}
              y={rect.y}
              width={rect.width}
              height={rect.height}
              style={{ stroke: 'aqua', fill: 'none', opacity: 0.3 }}
            />
          );
        })}
    </g>
  );
}
